import { Payments } from "@mui/icons-material";
import { Box, MenuItem, TextField } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { CustomerAppLabel } from "../../components/CustomerAppLabel";
import { DateTimeLabel } from "../../components/DateTimeLabel";
import { HeaderPage } from "../../components/Header/HeaderPage";
import PageWrapper from "../../components/PageWrapper/index";
import TableDataGrid from "../../components/TableDataGrid";
import Toast from "../../components/Toast";
import { WithdrawalAnalysisModal } from "../../components/WithdrawalAnalysisModal";
import { WithdrawalApproveConfirmModal } from "../../components/WithdrawalApproveConfirmModal";
import { WithdrawalStatusLabel } from "../../components/WithdrawalStatusLabel";
import { WithdrawalTypeLabel } from "../../components/WithdrawalTypeLabel";
import { formatCurrencyToShow } from "../../helpers/currency";
import { usePaginatedData } from "../../hooks/usePaginatedData";
import { useToast } from "../../hooks/useToast";
import { useUpdateWithdrawalStatus } from "../../hooks/useUpdateWithdrawalStatus";
import { Withdrawal } from "../../interfaces/withdrawal";
import { ActionMenu } from "./ActionMenu";

export default function AcquirerWithdrawals() {
    const [openModal, setOpenModal] = useState(false);
    const [openChangeAmountModal, setOpenChangeAmountModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState<Withdrawal | null>(null);
    const [filter, setFilter] = useState<string>("");

    const { toast, closeToast, updateToast } = useToast();

    const { data, isLoading, setPage, meta, refresh } =
        usePaginatedData<Withdrawal>(`/withdrawal?status=${filter}`);

    const handleEditSuccess = () => {
        refresh();

        updateToast({
            open: true,
            severity: "success",
            text: `Solicitação enviada com sucesso.`,
        });
    };

    const handleEditError = (data: Withdrawal | null, error?: string) => {
        updateToast({
            open: true,
            severity: "error",
            text: error,
        });
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRow(null);
    };

    const { isSubmitting, mutate } = useUpdateWithdrawalStatus<null>({
        onSuccess: handleEditSuccess,
        onError: handleEditError,
        onFinally: handleCloseModal,
    });

    const handleEdit = (row: Withdrawal) => {
        setSelectedRow(row);
        setOpenModal(true);
    };

    const handleOnClickApprove = (row: Withdrawal) => {
        mutate(row.id, "approve", row);
        setSelectedRow(row);
    };

    const handleOnClickReject = (row: Withdrawal) => {
        mutate(row.id, "reject");
        setSelectedRow(row);
    };

    const handleApproveConfirm = (row: Withdrawal) => {
        if (row?.type === "manager") {
            setOpenChangeAmountModal(true);
            setSelectedRow(row);
        } else {
            handleOnClickApprove(row);
        }
    };

    const handleApproveCancel = () => {
        setOpenChangeAmountModal(false);
        setSelectedRow(null);
    };

    const columns: GridColDef<Withdrawal>[] = [
        {
            field: "created_at",
            headerName: "Data/Hora",
            sortable: false,
            flex: 1,
            renderCell({ value }) {
                return <DateTimeLabel value={value} />;
            },
        },
        {
            field: "customer.name",
            headerName: "Cliente/App",
            sortable: false,
            flex: 1,
            renderCell: ({ row }) => (
                <CustomerAppLabel
                    customer={row.customer?.name || ""}
                    app={row.customer?.project_name || ""}
                />
            ),
        },
        {
            field: "type",
            headerName: "Solicitante",
            sortable: false,
            flex: 1,
            renderCell: ({ value }) => <WithdrawalTypeLabel value={value} />,
        },
        {
            field: "recipient_name",
            headerName: "Destinatário",
            sortable: false,
            flex: 1,
        },
        {
            field: "amount",
            headerName: "Valor",
            sortable: false,
            flex: 1,
            valueFormatter(params) {
                return `R$${formatCurrencyToShow(params.value)}`;
            },
        },
        {
            field: "status",
            headerName: "Status",
            sortable: false,
            flex: 1,
            renderCell: ({ value }) => <WithdrawalStatusLabel value={value} />,
        },
        {
            field: "actions",
            headerName: "Ações",
            sortable: false,
            disableExport: true,
            align: "right",
            headerAlign: "right",
            width: 80,
            renderCell: ({ row }) => (
                <ActionMenu
                    data={row}
                    loading={!!isSubmitting && selectedRow?.id === row.id}
                    onClickDetails={() => handleEdit(row)}
                    onClickApprove={() => handleApproveConfirm(row)}
                    onClickReject={() => handleOnClickReject(row)}
                />
            ),
        },
    ];

    return (
        <PageWrapper>
            <Box display="flex" justifyContent="space-between" mb={3}>
                <HeaderPage
                    title="Solicitações de saque"
                    subtitle="Aprovar ou cancela as solicitações de saque"
                    icon={<Payments />}
                />

                <Box display="flex" alignItems="flex-end" gap={2}>
                    <TextField
                        select
                        defaultValue=""
                        SelectProps={{ displayEmpty: true }}
                        sx={{ minWidth: 200 }}
                        onChange={(e) => setFilter(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="approved">Aprovado</MenuItem>
                        <MenuItem value="rejected">Recusado</MenuItem>
                        <MenuItem value="pending">Pendente</MenuItem>
                    </TextField>
                </Box>
            </Box>

            <Box mt={5}>
                <div className="containerListTablePagesGlobal">
                    <TableDataGrid
                        rows={data}
                        columns={columns}
                        loading={isLoading}
                        hideColumnMenu
                        hideExports
                        hideFilter
                        paginationMode="server"
                        rowCount={meta.total}
                        page={meta.current_page - 1}
                        onPageChange={(newPage) => setPage(newPage + 1)}
                        pageSize={meta.per_page}
                    />
                </div>
            </Box>

            <WithdrawalAnalysisModal
                open={openModal}
                onClose={handleCloseModal}
                data={selectedRow}
                onSuccess={handleEditSuccess}
                onError={handleEditError}
            />

            <WithdrawalApproveConfirmModal
                open={openChangeAmountModal}
                data={selectedRow}
                onCancel={handleApproveCancel}
                onConfim={(data) => handleOnClickApprove(data)}
            />

            <Toast
                open={toast.open}
                onClose={closeToast}
                severity={toast.severity}
                text={toast?.text}
            />
        </PageWrapper>
    );
}
